import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { appLoginUrl, appSignupUrl } from '../../config';
import { useMountEffect } from '../../hooks/useMountEffect';
import BrandLogoNg from './BrandLogoNg';

interface NgMobileMenuProps {
    links: { to: string; label: string }[];
    ctaTag: string;
    onClose: () => void;
}

const NgMobileMenu: React.FC<NgMobileMenuProps> = ({ links, ctaTag, onClose }) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const reduced = useReducedMotion();

    useMountEffect(() => {
        const menu = menuRef.current;
        const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const previousOverflow = document.body.style.overflow;
        const siblings = Array.from(menu?.parentElement?.children || [])
            .filter((element) => element !== menu && element instanceof HTMLElement);
        const previouslyInert = siblings.map((element) => element.hasAttribute('inert'));
        siblings.forEach((element) => element.setAttribute('inert', ''));
        document.body.style.overflow = 'hidden';
        menu?.querySelector<HTMLButtonElement>('button')?.focus();
        return () => {
            siblings.forEach((element, index) => {
                if (!previouslyInert[index]) element.removeAttribute('inert');
            });
            document.body.style.overflow = previousOverflow;
            previousFocus?.focus();
        };
    });

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
        }
        if (event.key === 'Tab') {
            const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('a[href], button'));
            const first = items[0];
            const last = items[items.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first?.focus();
            }
        }
    };

    return (
        <motion.div ref={menuRef} className="ngl-menu" role="dialog" aria-modal="true" aria-label="Main menu" onKeyDown={handleKeyDown}
            initial={reduced ? false : { opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduced ? 0 : 0.3 }}>
            <div className="ngl-menu-top">
                <Link to="/" className="ngl-nav-logo" aria-label="comtev home" onClick={onClose}><BrandLogoNg height={24} /></Link>
                <button type="button" className="ngl-nav-burger" style={{ display: 'inline-flex' }} aria-label="Close menu" onClick={onClose}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" /></svg>
                </button>
            </div>
            <nav className="ngl-menu-links" aria-label="Menu">{links.map((link, i) => <Link key={link.to} to={link.to} className="ngl-menu-link" onClick={onClose}>{link.label}<small>{String(i + 1).padStart(2, '0')}</small></Link>)}</nav>
            <div className="ngl-menu-foot"><a href={appSignupUrl()} className="ngl-btn" data-cta={`${ctaTag}-menu`}>start free</a><a href={appLoginUrl()} className="ngl-textlink">log in</a></div>
        </motion.div>
    );
};

export default NgMobileMenu;
